import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import {
  FaTrashAlt,
  FaUndo,
  FaSearch,
  FaSyncAlt,
  FaReceipt,
  FaThLarge,
  FaList,
  FaClock,
  FaTable,
  FaShoppingBag,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPrint,
} from "react-icons/fa";

import { formatStoredMoney } from "../utils/currency";
import { API_BASE } from "../config/constants";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import { printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  listOfflineDeletedOrders,
  restoreOfflineDeletedOrder,
} from "../offline/orderStore";

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const DeletedOrdersPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [restaurantId, setRestaurantId] = useState(null);
  const [confirmingLog, setConfirmingLog] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'
  const reportRef = useRef(null);

  const fetchLogs = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const stored = localStorage.getItem("userData");
      const parsed = stored ? JSON.parse(stored) : null;
      const id = parsed?.data?.id || parsed?.id;
      setRestaurantId(id || null);

      if (!id) {
        setLogs([]);
        return;
      }

      if (isFullOfflineModeEnabled()) {
        const result = await listOfflineDeletedOrders(id);
        setLogs(Array.isArray(result) ? result : []);
      } else {
        const res = await fetch(`${API_BASE}/deleted-orders/list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: id }),
        });
        const result = await res.json();
        if (!res.ok) {
          throw new Error(result?.message || "Failed to fetch deleted orders");
        }
        setLogs(Array.isArray(result?.data) ? result.data : []);
      }
    } catch (error) {
      toast.error(error?.message || "Network error");
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const restoreOrder = async (logId) => {
    if (!restaurantId) return;
    try {
      setRestoringId(logId);
      let result;
      if (isFullOfflineModeEnabled()) {
        result = await restoreOfflineDeletedOrder(restaurantId, logId);
      } else {
        const res = await fetch(`${API_BASE}/deleted-orders/restore`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, log_id: logId }),
        });
        result = await res.json();
        if (!res.ok) {
          throw new Error(result?.message || "Failed to restore order");
        }
      }
      toast.success(result?.message || "Order restored successfully");
      fetchLogs(true);
    } catch (error) {
      toast.error(error?.message || "Failed to restore order");
    } finally {
      setRestoringId(null);
    }
  };

  const confirmRestore = (log) => setConfirmingLog(log);
  const cancelRestore = () => setConfirmingLog(null);
  const handleConfirmRestore = () => {
    if (confirmingLog) {
      restoreOrder(confirmingLog.logId);
      setConfirmingLog(null);
    }
  };

  // Filtered Logs
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return logs;
    const q = searchQuery.toLowerCase().trim();
    return logs.filter((item) => {
      const order = item?.order || {};
      const code = String(
        order?.ordercode || order?.order_code || order?.id || item?.order_id || ""
      ).toLowerCase();
      const table = String(order?.tableid || order?.table_id || "").toLowerCase();
      const mode = String(order?.mode || order?.paymentmode || "").toLowerCase();
      const amount = String(
        order?.totalamount ?? order?.total_amount ?? order?.amount ?? ""
      );
      return (
        code.includes(q) ||
        table.includes(q) ||
        mode.includes(q) ||
        amount.includes(q)
      );
    });
  }, [logs, searchQuery]);

  // Calculated Stats
  const stats = useMemo(() => {
    const totalCount = logs.length;
    const totalAmount = logs.reduce((sum, item) => {
      const order = item?.order || {};
      const amt = Number(order?.totalamount ?? order?.total_amount ?? order?.amount ?? 0) || 0;
      return sum + amt;
    }, 0);
    return { totalCount, totalAmount };
  }, [logs]);

  return (
    <Container>
      <Wrapper>
        <ReportPageHeader
          title="Deleted Orders"
          subtitle="View and restore deleted bills and orders."
          stats={[
            { label: "Deleted Orders", value: stats.totalCount },
            { label: "Total Deleted Amount", value: formatStoredMoney(stats.totalAmount) },
          ]}
        />
        <ReportControlBar>
          <HeaderActions>
            <SearchBox>
              <FaSearch />
              <input
                type="text"
                placeholder="Search order #, table, mode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearSearchBtn onClick={() => setSearchQuery("")}>
                  ×
                </ClearSearchBtn>
              )}
            </SearchBox>

            <ViewModeGroup>
              <ViewToggleBtn
                $active={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <FaThLarge />
              </ViewToggleBtn>
              <ViewToggleBtn
                $active={viewMode === "table"}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaList />
              </ViewToggleBtn>
            </ViewModeGroup>

            <RefreshBtn
              type="button"
              onClick={() => {
                setRefreshing(true);
                fetchLogs(true);
              }}
              disabled={refreshing}
            >
              <FaSyncAlt className={refreshing ? "spin" : ""} />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </RefreshBtn>
            <RefreshBtn
              type="button"
              onClick={() => printThermal(reportRef.current, "deleted_orders")}
              disabled={!filteredLogs.length}
            >
              <FaPrint /> <span>Thermal Print</span>
            </RefreshBtn><RefreshBtn
              type="button"
              onClick={() => printThermalViaUsb(reportRef.current, "deleted_orders")}
              disabled={!filteredLogs.length}
            >
              <FaPrint /> <span>Thermal Print via USB</span>
            </RefreshBtn>
          </HeaderActions>
        </ReportControlBar>

        <div ref={reportRef}>
        {/* Main Content View */}
        {loading ? (
          <LoaderWrap>
            <Spinner />
            <LoaderText>Loading deleted orders...</LoaderText>
          </LoaderWrap>
        ) : filteredLogs.length === 0 ? (
          <EmptyState>
            <EmptyIconWrap>
              <FaTrashAlt />
            </EmptyIconWrap>
            <EmptyTitle>
              {searchQuery ? "No matching orders found" : "No deleted orders found"}
            </EmptyTitle>
            <EmptySub>
              {searchQuery
                ? `No deleted orders match "${searchQuery}". Try clearing search.`
                : "Deleted orders will appear here once they are removed."}
            </EmptySub>
          </EmptyState>
        ) : viewMode === "grid" ? (
          <CardGrid>
            {filteredLogs.map((item) => {
              const order = item?.order || {};
              const orderCode =
                order?.ordercode ||
                order?.order_code ||
                order?.id ||
                item?.order_id;
              const tableId = order?.tableid || order?.table_id || "-";
              const amount =
                order?.totalamount ?? order?.total_amount ?? order?.amount ?? 0;
              const mode = order?.mode || order?.paymentmode;
              const isBusy = restoringId === item?.log_id;

              return (
                <OrderCard key={item?.log_id}>
                  <CardHeaderRow>
                    <OrderTag>
                      <FaReceipt />
                      <span>Order #{orderCode || "-"}</span>
                    </OrderTag>
                    <DeletedDate>
                      <FaClock />
                      <span>{formatDateTime(item?.deleted_at)}</span>
                    </DeletedDate>
                  </CardHeaderRow>

                  <CardDetailsGrid>
                    <DetailItem>
                      <DetailLabel>
                        <FaTable /> Table
                      </DetailLabel>
                      <DetailVal>{tableId}</DetailVal>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>
                        <FaShoppingBag /> Items
                      </DetailLabel>
                      <DetailVal>{item?.items_count ?? 0}</DetailVal>
                    </DetailItem>

                    {mode ? (
                      <DetailItem>
                        <DetailLabel>Mode</DetailLabel>
                        <ModeBadge>{mode}</ModeBadge>
                      </DetailItem>
                    ) : null}
                  </CardDetailsGrid>

                  <CardFooterRow>
                    <AmountWrap>
                      <AmountLabel>Amount</AmountLabel>
                      <AmountVal>{formatStoredMoney(amount)}</AmountVal>
                    </AmountWrap>

                    <RestoreBtn
                      type="button"
                      onClick={() =>
                        confirmRestore({ logId: item?.log_id, orderCode })
                      }
                      disabled={isBusy}
                    >
                      {isBusy ? (
                        <>
                          <SmallSpinner />
                          <span>Restoring...</span>
                        </>
                      ) : (
                        <>
                          <FaUndo />
                          <span>Restore</span>
                        </>
                      )}
                    </RestoreBtn>
                  </CardFooterRow>
                </OrderCard>
              );
            })}
          </CardGrid>
        ) : (
          <TableContainer>
            <StyledTable>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Deleted Date & Time</th>
                  <th>Table</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th style={{ textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((item) => {
                  const order = item?.order || {};
                  const orderCode =
                    order?.ordercode ||
                    order?.order_code ||
                    order?.id ||
                    item?.order_id;
                  const tableId = order?.tableid || order?.table_id || "-";
                  const amount =
                    order?.totalamount ?? order?.total_amount ?? order?.amount ?? 0;
                  const mode = order?.mode || order?.paymentmode;
                  const isBusy = restoringId === item?.log_id;

                  return (
                    <tr key={item?.log_id}>
                      <td>
                        <OrderCodePill>#{orderCode || "-"}</OrderCodePill>
                      </td>
                      <td>
                        <DateCell>
                          <FaClock />
                          <span>{formatDateTime(item?.deleted_at)}</span>
                        </DateCell>
                      </td>
                      <td>
                        <strong>{tableId}</strong>
                      </td>
                      <td>{item?.items_count ?? 0} items</td>
                      <td>
                        <TableAmount>{formatStoredMoney(amount)}</TableAmount>
                      </td>
                      <td>{mode ? <ModeBadge>{mode}</ModeBadge> : "-"}</td>
                      <td style={{ textAlign: "right" }}>
                        <TableRestoreBtn
                          type="button"
                          onClick={() =>
                            confirmRestore({ logId: item?.log_id, orderCode })
                          }
                          disabled={isBusy}
                        >
                          {isBusy ? (
                            <>
                              <SmallSpinner />
                              <span>Restoring...</span>
                            </>
                          ) : (
                            <>
                              <FaUndo />
                              <span>Restore</span>
                            </>
                          )}
                        </TableRestoreBtn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </StyledTable>
          </TableContainer>
        )}
        </div>
      </Wrapper>

      {/* Confirmation Modal */}
      {confirmingLog ? (
        <ModalOverlay role="dialog" aria-modal="true">
          <ModalCard>
            <ModalHeader>
              <ModalWarningIcon>
                <FaExclamationTriangle />
              </ModalWarningIcon>
              <ModalTitle>Restore Order?</ModalTitle>
              {confirmingLog?.orderCode ? (
                <ModalSub>Order #{confirmingLog.orderCode}</ModalSub>
              ) : null}
            </ModalHeader>

            <ModalBody>
              Do you want to restore this order as a new active bill? It will be re-added to your active orders.
            </ModalBody>

            <ModalActions>
              <ModalCancelBtn type="button" onClick={cancelRestore}>
                Cancel
              </ModalCancelBtn>
              <ModalConfirmBtn type="button" onClick={handleConfirmRestore}>
                <FaCheckCircle />
                <span>Confirm Restore</span>
              </ModalConfirmBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Container>
  );
};

export default DeletedOrdersPage;

/* ===== STYLED COMPONENTS ===== */

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px;
  display: flex;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1240px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const TitleBadgeIcon = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.12);
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
`;

const PageSub = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  color: #64748b;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 12px;
  min-width: 260px;
  transition: all 0.2s ease;

  svg {
    color: #94a3b8;
    font-size: 14px;
    margin-right: 8px;
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    padding: 9px 0;
    font-size: 13px;
    color: #0f172a;
    width: 100%;
    &::placeholder {
      color: #94a3b8;
    }
  }

  &:focus-within {
    border-color: var(--app-primary);
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
  }
`;

const ClearSearchBtn = styled.button`
  border: none;
  background: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover {
    color: #475569;
  }
`;

const ViewModeGroup = styled.div`
  display: flex;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 3px;
`;

const ViewToggleBtn = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? "#0f172a" : "transparent")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#64748b")};
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ $active }) => ($active ? "#ffffff" : "#0f172a")};
  }
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${({ $bg }) => $bg || "#f1f5f9"};
  color: ${({ $color }) => $color || "#0f172a"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 2px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 18px;
`;

const OrderCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
    border-color: #cbd5e1;
  }
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const OrderTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  color: #0f172a;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 800;

  svg {
    color: var(--app-primary);
    font-size: 13px;
  }
`;

const DeletedDate = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;

  svg {
    font-size: 11px;
  }
`;

const CardDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DetailVal = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
`;

const ModeBadge = styled.span`
  display: inline-block;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  text-transform: capitalize;
  width: fit-content;
`;

const CardFooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
`;

const AmountWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const AmountLabel = styled.span`
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
`;

const AmountVal = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
`;

const RestoreBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary));
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(2, 132, 199, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const TableContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.04);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;

  th {
    background: #f8fafc;
    color: #64748b;
    font-weight: 700;
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.04em;
  }

  td {
    padding: 14px 18px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background: #f8fafc;
  }
`;

const OrderCodePill = styled.span`
  font-weight: 800;
  color: #0f172a;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
`;

const DateCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  svg {
    font-size: 12px;
    color: #94a3b8;
  }
`;

const TableAmount = styled.span`
  font-weight: 800;
  color: #0f172a;
  font-size: 14px;
`;

const TableRestoreBtn = styled(RestoreBtn)`
  padding: 8px 14px;
  font-size: 12px;
  display: inline-flex;
`;

const LoaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 12px;
`;

const spinAnim = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 4px solid var(--app-primary-soft);
  border-top-color: var(--app-primary);
  border-radius: 50%;
  animation: ${spinAnim} 0.8s linear infinite;
`;

const SmallSpinner = styled(Spinner)`
  width: 14px;
  height: 14px;
  border-width: 2px;
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
`;

const LoaderText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
`;

const EmptyState = styled.div`
  background: #ffffff;
  border: 2px dashed #cbd5e1;
  border-radius: 20px;
  padding: 60px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
`;

const EmptyIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
`;

const EmptySub = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: #64748b;
  max-width: 380px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes modalPop {
    from {
      opacity: 0;
      transform: scale(0.94) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalWarningIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 12px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
`;

const ModalSub = styled.span`
  margin-top: 4px;
  font-size: 13px;
  font-weight: 700;
  color: var(--app-primary);
  background: #f0f9ff;
  padding: 2px 10px;
  border-radius: 6px;
`;

const ModalBody = styled.p`
  margin: 16px 0 24px;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const ModalCancelBtn = styled.button`
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  border-radius: 14px;
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const ModalConfirmBtn = styled.button`
  border: none;
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary));
  color: #ffffff;
  border-radius: 14px;
  padding: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.3);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(2, 132, 199, 0.4);
  }
`;
